import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import uiStyle from '@/components/ui/ui.module.css';
import { FundClassification } from '@/models/common/FundClassification';
import { FundManagementDetailsDtoIn } from '@/models/in/FundManagementDetailsDtoIn';
import { buildUpdateDtoFromFund } from './fundViewForm';
import useUpdateFund from './useUpdateFund';
import styles from './FundDialogs.module.css';

interface EditFundDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  fund: FundManagementDetailsDtoIn;
}

const EditFundDetailsDialog = ({ open, onClose, fund }: EditFundDetailsDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(fund.name);
  const [classification, setClassification] = useState(fund.classification);
  const [internalClassification, setInternalClassification] = useState(fund.internalClassification ?? '');
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { save, isSaving, serverError, clearServerError } = useUpdateFund(String(fund.id), onClose);

  useEffect(() => {
    if (open) {
      setName(fund.name);
      setClassification(fund.classification);
      setInternalClassification(fund.internalClassification ?? '');
      setNote('');
      setValidationError(null);
      clearServerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fund]);

  const handleSave = () => {
    if (!name.trim()) {
      setValidationError('fundsManagement.validation.nameRequired');
      return;
    }
    if (!note.trim()) {
      setValidationError('fundsManagement.noteDialog.noteRequired');
      return;
    }
    setValidationError(null);
    save(buildUpdateDtoFromFund(fund, {
      name: name.trim(),
      classification,
      internalClassification: internalClassification.trim() || null,
    }, note.trim()));
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('fundsManagement.editDetailsTitle')}
    >
      <div className={styles.dialogForm}>
        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.fundName')}</label>
          <Input className="w-full" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.classification')}</label>
          <select
            className={uiStyle['input-style']}
            value={classification}
            onChange={(e) => setClassification(e.target.value as FundClassification)}
          >
            {Object.values(FundClassification).map((value) => (
              <option key={value} value={value}>
                {t(`fundsManagement.classificationNames.${value}`)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.internalClassification')}</label>
          <Input className="w-full" value={internalClassification} onChange={(e) => setInternalClassification(e.target.value)} />
        </div>
        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.noteDialog.noteLabel')}</label>
          <textarea
            className={styles.dialogTextarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('fundsManagement.noteDialog.notePlaceholder')}
          />
        </div>

        {validationError && <p className={styles.formError}>{t(validationError)}</p>}
        {serverError && <p className={styles.formError}>{serverError}</p>}

        <div className={styles.formActions}>
          <Button variant="white" onClick={onClose} disabled={isSaving}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('loading') : t('fundsManagement.save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditFundDetailsDialog;
